/**
 * Every value a register writer sends or a register reader shows (13-18), proven once without SharePoint.
 *
 * - **Writers** (FIELDSET §1.4, the exact Graph payload forms): `toInstant`, `dayNew`, `dayExisting`,
 *   `melbourneWallToInstant`, `todayMelbourne`, `notInFuture`. No writer ever sends a naive wall-clock time
 *   (ISS-201: a Melbourne time with no zone is read as Pacific and lands +17 to +19 h late).
 * - **Readers** (`11-QMS-GAPS.md` §5): `melbourneDayOf`, `classifyInstant`. 00:00Z (FieldLink, `dayExisting`) and
 *   Pacific midnight (SharePoint date-only writes) both read as the intended day; any other time reads as its
 *   Melbourne day, and `classifyInstant` lets a time of day on a day column be flagged, never "corrected".
 * - **Display** (D-13-L-10): `formatDay` → `6 Feb 2026`, `formatInstant` → `5 Oct 2026, 9:30 am` (Melbourne), so
 *   ISS-239's `M/D/YYYY` misread can't happen on anything built on this.
 * - **Input checks** (FIELDSET §3.1, the office lodge too): `cleanText` (FieldLink's D-12.1-14-7 handling; over the
 *   cap is refused, never truncated), `checkChoice` (exact, never coerced), `checkUpn`.
 *
 * Every Melbourne calculation goes through `Intl.DateTimeFormat` with `timeZone: 'Australia/Melbourne'`; there is
 * no date library and no fixed offset, and nothing reads the process zone. Pure: no React, no Graph, no
 * `Date.now()` (callers pass `now`). Invalid or empty reader input gives `null` (display: `—`), never a throw.
 *
 * This module imports nothing from `config/`, so a page that only formats dates does not pull the 139 kB
 * `liveColumns.json` into its chunk (D-13-18-4). Choices are passed in (from 13-17's `choicesOf`), and
 * `fieldValues.test.ts` checks `TEXT_CAPS` against the field set's live caps.
 *
 * `actionVerbs.ts` keeps its own `melbourneDay` for now; a later refactor may point it here.
 */

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

/** A checked value, or the reason it was refused. A refusal never carries a value. */
export type Checked<T, Code extends string> = { ok: true; value: T } | { ok: false; code: Code };

/** `cleanText`'s refusal for an over-cap value: the length after cleaning and the cap it broke. */
export interface TooLong {
  ok: false;
  code: 'too-long';
  length: number;
  cap: number;
}

export type CleanTextResult = { ok: true; value: string } | TooLong | { ok: false; code: 'not-text' };

export type WallTimeResult =
  | { ok: true; value: string; ambiguous: boolean }
  | { ok: false; code: 'invalid-day' | 'invalid-time' | 'nonexistent-time' };

/** How an instant on a day column was written (`11-QMS-GAPS.md` §5). */
export type InstantClass = 'utc-midnight' | 'pacific-midnight' | 'time-of-day';

/** FIELDSET §3.1 caps: a text field ≤ 255 characters, a text area ≤ 4,000. */
export const TEXT_CAPS = { text: 255, textarea: 4000 } as const;

/** What a missing or unreadable date shows. */
export const NO_DATE = '—';

// ---------------------------------------------------------------------------
// Zone arithmetic (Intl only)
// ---------------------------------------------------------------------------

const MELBOURNE = 'Australia/Melbourne';
/** The Q&S site's zone, used only to recognise a SharePoint date-only write (§5 row 2). */
const PACIFIC = 'America/Los_Angeles';

interface WallParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = FORMATTERS.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-AU', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    FORMATTERS.set(timeZone, f);
  }
  return f;
}

/** The wall-clock parts of an epoch millisecond in a zone. */
function wallParts(ms: number, timeZone: string): WallParts {
  const parts = formatterFor(timeZone).formatToParts(new Date(ms));
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    // Some engines print midnight as 24 even under h23; it is the same instant's hour 0.
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const dayText = (p: Pick<WallParts, 'year' | 'month' | 'day'>) =>
  `${String(p.year).padStart(4, '0')}-${pad2(p.month)}-${pad2(p.day)}`;

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
/** An instant must name its zone (`Z` or an offset). A naive date-time is never read (it would use the process zone). */
const INSTANT_RE = /^(\d{4}-\d{2}-\d{2})T([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(?:\.\d{1,9})?)?(Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/;

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** A `YYYY-MM-DD` calendar day, checked (month 1–12, the day within that month), or `null`. */
function parseDay(value: unknown): { year: number; month: number; day: number } | null {
  if (typeof value !== 'string') return null;
  const m = DAY_RE.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

/** An instant string with an explicit zone, as epoch ms, or `null`. The calendar day must exist. */
function parseInstant(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const m = INSTANT_RE.exec(value);
  if (!m || !parseDay(m[1])) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

// ---------------------------------------------------------------------------
// Writers (FIELDSET §1.4)
// ---------------------------------------------------------------------------

/**
 * An instant as the §1.4 payload: `"2026-10-05T04:39:41Z"` (ends `Z`, no offset, seconds; milliseconds dropped).
 * Throws `RangeError` on an invalid `Date`: a writer never sends one.
 */
export function toInstant(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** A day for a new `dateOnly` column: the intended Melbourne day as text, never through a `Date`. */
export function dayNew(day: string): Checked<string, 'invalid-day'> {
  return parseDay(day) ? { ok: true, value: day } : { ok: false, code: 'invalid-day' };
}

/** A day for an existing `dateTime` column with no time: UTC midnight of the intended Melbourne day (FieldLink's). */
export function dayExisting(day: string): Checked<string, 'invalid-day'> {
  return parseDay(day) ? { ok: true, value: `${day}T00:00:00Z` } : { ok: false, code: 'invalid-day' };
}

/** The offsets Melbourne can be on (AEST, AEDT), as candidates only: each is kept only if `Intl` round-trips it. */
const MELBOURNE_CANDIDATE_OFFSETS_H = [11, 10] as const;

/**
 * A Melbourne wall time (`YYYY-MM-DD`, `HH:mm` 24 h) → the §1.4 instant, by the `Australia/Melbourne` rules.
 *
 * Each candidate offset is tried and kept only when the instant's Melbourne parts round-trip to the entered day and
 * time. Two survivors = the repeated hour at the end of daylight saving: the **first** occurrence is taken and
 * `ambiguous` is `true`, so the form can say so (D-13-18-1). None = the skipped hour at the start: refused.
 */
export function melbourneWallToInstant(day: string, time: string): WallTimeResult {
  const d = parseDay(day);
  if (!d) return { ok: false, code: 'invalid-day' };
  const t = typeof time === 'string' ? TIME_RE.exec(time) : null;
  if (!t) return { ok: false, code: 'invalid-time' };
  const hour = Number(t[1]);
  const minute = Number(t[2]);
  const wallAsUtc = Date.UTC(d.year, d.month - 1, d.day, hour, minute, 0);

  const matches: number[] = [];
  for (const offset of MELBOURNE_CANDIDATE_OFFSETS_H) {
    const ms = wallAsUtc - offset * 3_600_000;
    const p = wallParts(ms, MELBOURNE);
    if (p.year === d.year && p.month === d.month && p.day === d.day && p.hour === hour && p.minute === minute) {
      matches.push(ms);
    }
  }
  if (matches.length === 0) return { ok: false, code: 'nonexistent-time' };
  const first = Math.min(...matches);
  return { ok: true, value: toInstant(new Date(first)), ambiguous: matches.length > 1 };
}

/**
 * An instant's Melbourne wall clock (13-27's deadlines): its day `YYYY-MM-DD`, its `HH:MM` and its seconds. `null`
 * when the value is unreadable or names no zone (never guessed, D-13-18-5). Melbourne's offsets are whole hours, so
 * the minutes and seconds are the instant's own; the day and hour come from `Intl` by the `Australia/Melbourne` rules.
 */
export function melbourneWallOf(value: string | null | undefined): { day: string; time: string; seconds: number } | null {
  const ms = parseInstant(value);
  if (ms === null) return null;
  const p = wallParts(ms, MELBOURNE);
  return { day: dayText(p), time: `${pad2(p.hour)}:${pad2(p.minute)}`, seconds: p.second };
}

/** Today's Melbourne calendar day as `YYYY-MM-DD`. */
export function todayMelbourne(now: Date): string {
  return dayText(wallParts(now.getTime(), MELBOURNE));
}

/**
 * "Not in the future", for office writers (D-13-18-2):
 * - a day (`YYYY-MM-DD`), or a day stored as an instant (00:00Z or Pacific midnight, §5), is judged by its
 *   Melbourne day against today in Melbourne, so today is never the future;
 * - an instant with a time of day is judged against `now` itself (a time later today is the future).
 */
export function notInFuture(value: string, now: Date): Checked<string, 'invalid-date' | 'in-future'> {
  if (parseDay(value)) {
    return value <= todayMelbourne(now) ? { ok: true, value } : { ok: false, code: 'in-future' };
  }
  const ms = parseInstant(value);
  if (ms === null) return { ok: false, code: 'invalid-date' };
  if (classifyInstant(value) === 'time-of-day') {
    return ms <= now.getTime() ? { ok: true, value } : { ok: false, code: 'in-future' };
  }
  const day = dayText(wallParts(ms, MELBOURNE));
  return day <= todayMelbourne(now) ? { ok: true, value } : { ok: false, code: 'in-future' };
}

// ---------------------------------------------------------------------------
// Readers (11-QMS-GAPS.md §5)
// ---------------------------------------------------------------------------

/**
 * The Melbourne calendar day of a stored value, or `null` (invalid or empty; never a throw).
 * An instant → its Melbourne day (00:00Z and Pacific midnight are the intended day by construction). A plain
 * `YYYY-MM-DD` is already a Melbourne day. A naive date-time (no zone) is refused, never guessed.
 */
export function melbourneDayOf(value: string | null | undefined): string | null {
  if (parseDay(value)) return value as string;
  const ms = parseInstant(value);
  return ms === null ? null : dayText(wallParts(ms, MELBOURNE));
}

/** How an instant was written: `utc-midnight`, `pacific-midnight` (by the Pacific zone's rules), or `time-of-day`. */
export function classifyInstant(value: string | null | undefined): InstantClass | null {
  const ms = parseInstant(value);
  if (ms === null) return null;
  if (ms % 86_400_000 === 0) return 'utc-midnight';
  if (ms % 1000 === 0) {
    const p = wallParts(ms, PACIFIC);
    if (p.hour === 0 && p.minute === 0 && p.second === 0) return 'pacific-midnight';
  }
  return 'time-of-day';
}

// ---------------------------------------------------------------------------
// Display (D-13-L-10)
// ---------------------------------------------------------------------------

/** Fixed three-letter months: `Intl`'s en-AU short month is `Sept` on some engines. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

function dayLabel(year: number, month: number, day: number): string {
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

/** A day or an instant as `D MMM YYYY` (an instant by its Melbourne day); `—` when missing or unreadable. */
export function formatDay(value: string | null | undefined): string {
  const day = parseDay(melbourneDayOf(value));
  return day ? dayLabel(day.year, day.month, day.day) : NO_DATE;
}

/** An instant as `D MMM YYYY, h:mm am|pm` in Melbourne; `—` when missing or unreadable. */
export function formatInstant(value: string | null | undefined): string {
  const ms = parseInstant(value);
  if (ms === null) return NO_DATE;
  const p = wallParts(ms, MELBOURNE);
  const h12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${dayLabel(p.year, p.month, p.day)}, ${h12}:${pad2(p.minute)} ${p.hour < 12 ? 'am' : 'pm'}`;
}

// ---------------------------------------------------------------------------
// Input checks (FIELDSET §3.1; FieldLink's D-12.1-14-7 text handling)
// ---------------------------------------------------------------------------

// eslint-disable-next-line no-control-regex
const LINE_CONTROLS = /[\u0000-\u001F\u007F-\u009F]/g;
// eslint-disable-next-line no-control-regex
const AREA_CONTROLS = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/g; // keeps \t and \n
const BIDI_CONTROLS = /[‪-‮⁦-⁩]/g;

/**
 * Clean one text value and check its cap. Over the cap is **refused, never truncated** (a truncated record is a
 * silently altered record), and the cap applies after cleaning.
 * - C0, DEL and C1 control characters and the bidi controls are stripped;
 * - a text field turns each run of `\r`, `\n`, `\t` into one space; a text area keeps `\n` and `\t`
 *   (`\r\n` and `\r` → `\n`). The mode defaults to text area when `cap` is the text-area cap;
 * - trimmed; `null` / `undefined` / blank → `''` (required-ness is the verb's, FIELDSET §1.1);
 * - a non-string is refused `not-text`.
 * Length is in UTF-16 code units, as SharePoint counts a text column's length.
 */
export function cleanText(
  value: string | null | undefined,
  cap: number,
  opts: { multiline?: boolean } = {},
): CleanTextResult {
  if (value === null || value === undefined) return { ok: true, value: '' };
  if (typeof value !== 'string') return { ok: false, code: 'not-text' };
  const multiline = opts.multiline ?? cap === TEXT_CAPS.textarea;
  const stripped = multiline
    ? value.replace(/\r\n?/g, '\n').replace(AREA_CONTROLS, '')
    : value.replace(/[\r\n\t]+/g, ' ').replace(LINE_CONTROLS, '');
  const cleaned = stripped.replace(BIDI_CONTROLS, '').trim();
  if (cleaned.length > cap) return { ok: false, code: 'too-long', length: cleaned.length, cap };
  return { ok: true, value: cleaned };
}

/**
 * An exact match against a live choice list (pass 13-17's `choicesOf(register, column)`). Case, spacing or an
 * XML-escaped `&amp;` is refused `unknown-choice`, never coerced. A blank is not a choice: skip an unanswered
 * optional choice before calling.
 */
export function checkChoice(value: string, choices: readonly string[]): Checked<string, 'unknown-choice'> {
  return typeof value === 'string' && choices.includes(value)
    ? { ok: true, value }
    : { ok: false, code: 'unknown-choice' };
}

const UPN_RE = /^[^\s@,;<>()"'[\]\\]+@[^\s@,;<>()"'[\]\\]+\.[^\s@,;<>()"'[\]\\.]+$/;

/**
 * A UPN as the registers store it (D-12.1-12.1-9): one address, trimmed and lower-cased. A guest account
 * (`#EXT#`) is refused `guest-upn`; a list, a display name, or anything that is not one address is `invalid-upn`.
 */
export function checkUpn(value: string): Checked<string, 'invalid-upn' | 'guest-upn'> {
  if (typeof value !== 'string') return { ok: false, code: 'invalid-upn' };
  const v = value.trim().toLowerCase();
  if (v.includes('#ext#')) return { ok: false, code: 'guest-upn' };
  return UPN_RE.test(v) ? { ok: true, value: v } : { ok: false, code: 'invalid-upn' };
}
